/** Shared attention-ledger hook. Every Memso surface that implements
 *  the curate-not-aggregate pattern emits through this so the nightly
 *  learner can reconstruct what the user saw and how they acted on it.
 *
 *  D1 (the Today page) shipped the first copy of this inline; A-series
 *  follow-up lifts it here and wires it into every surfacing feature
 *  (Recommendations, Observations, Engagements, Awaiting You, Recall,
 *  Library, People dossier, Trip dossier). Each surface passes its own
 *  stable `surface` string so the ledger is segmentable per feature.
 *
 *  All write paths swallow errors: a ledger failure must never block
 *  the user's gesture. The next emit succeeds once connectivity is
 *  back. View events batch through one round trip on list render. */
import { useCallback, useEffect, useRef } from "react";
import { api } from "./api";

export type AttentionEvent =
  | "view"
  | "open_source"
  | "done"
  | "snooze"
  | "dismiss"
  | "accept"
  | "undo"
  | "resolved"
  | "ignored_for_24h"
  | "hide_forever";

export interface AttentionEmitArgs {
  cluster?: string | null;
  capsule?: string | null;
  itemKind: string;
  itemId: string;
  context?: Record<string, unknown> | null;
}

export interface AttentionViewItem {
  cluster?: string | null;
  capsule?: string | null;
  itemKind: string;
  itemId: string;
  context?: Record<string, unknown> | null;
}

export function useAttention(surface: string): {
  emit: (eventType: AttentionEvent, args: AttentionEmitArgs) => Promise<void>;
  emitViews: (items: AttentionViewItem[]) => Promise<void>;
} {
  const emit = useCallback(
    async (eventType: AttentionEvent, args: AttentionEmitArgs) => {
      try {
        await api.todayAttention({
          surface,
          cluster: args.cluster ?? null,
          capsule: args.capsule ?? null,
          item_kind: args.itemKind,
          item_id: args.itemId,
          event_type: eventType,
          context: args.context ?? null,
        });
      } catch {
        /* swallow: a ledger write must never block a gesture */
      }
    },
    [surface],
  );

  const emitViews = useCallback(
    async (items: AttentionViewItem[]) => {
      if (items.length === 0) return;
      try {
        await api.todayAttentionBatch(
          items.map((it) => ({
            surface,
            cluster: it.cluster ?? null,
            capsule: it.capsule ?? null,
            item_kind: it.itemKind,
            item_id: it.itemId,
            event_type: "view" as const,
            context: it.context ?? null,
          })),
        );
      } catch {
        /* swallow */
      }
    },
    [surface],
  );

  return { emit, emitViews };
}

/** Fire a single batched `view` emit when a surface's visible item set
 *  changes. Dedupes by the sorted id list so re-renders that don't
 *  change the rendered items never re-emit. Pass the items the surface
 *  is currently showing; the hook handles the round trip. */
export function useViewEmission(
  surface: string,
  items: AttentionViewItem[],
): void {
  const { emitViews } = useAttention(surface);
  const lastKeyRef = useRef<string>("");
  useEffect(() => {
    if (items.length === 0) return;
    const key = items
      .map((it) => `${it.itemKind}:${it.itemId}`)
      .sort()
      .join("|");
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    void emitViews(items);
  }, [surface, items, emitViews]);
}
